import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Checkbox,
  FormControlLabel,
  MenuItem,
  Skeleton,
  TextField,
} from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';

import { useAppSelector } from 'hooks/store';
import { ConfigurationSelector } from 'store/configuration';
import {
  CallScribePipelineStage,
  CallScribeTimeEntryStatus,
  ICallScribeCallListItem,
} from 'services/callScribe/types';
import { CallScribeDraftOutcome, patchCallScribeDraft } from 'services/callScribe';
import PostedTimeEntryEditor from 'components/CallScribe/PostedTimeEntryEditor';
import CallInsightsHeader from 'components/CallScribe/CallInsightsHeader';
import { sentimentChipFor } from 'components/CallScribe/callChips';
import { formatDurationLabel } from 'components/CallScribe/callLogColumns';
import TimeEntrySection from 'components/CallScribe/TimeEntrySection';
import ActionBar from 'components/CallScribe/ui/ActionBar';
import usePaperStyles from 'components/CallScribe/paperStyles';
import { useTimeEntrySubmit } from 'components/CallScribe/useTimeEntrySubmit';
import {
  destinationLine,
  failureLine,
  successLine,
} from 'components/CallScribe/noteOutcomes';
import { contactNoteText, readContactNote } from 'components/CallScribe/contactNote';
import { usePostCallIntelligence } from 'components/CallScribe/usePostCallIntelligence';
import {
  AUTO_POST_TITLE,
  CUSTOMER_NOTES_OFF,
  autoPostLine,
  NOTES_LOADING_LABEL,
  customerNotesState,
  fallbackDurationMinutes,
  postedDurationOf,
  isAwaitingAutomaticPost,
  isLoggedAttempt,
  isReviewActionable,
  resolveReviewPhase,
  showsManualSubmit,
  showsSubmitControl,
} from 'components/CallScribe/postCallReviewPhase';
import {
  formatPhoneE164ToDisplay,
  otherPartyPhone,
} from 'components/CallScribe/Recents/recentsHelpers';
import usePostCallReviewStyles from 'components/CallScribe/postCallReviewStyles';
import {
  SIP_REJECTION_ACTION,
  SIP_REJECTION_HEADLINE,
  sipRejectionDetail,
} from 'components/CallScribe/sipRejectionMessage';
import {
  TICKET_NUMBER_REQUIRED,
  ticketPromptFor,
  checkTicket,
  checkedTicketLabel,
  isUsableTicketNumber,
  type ICheckedTicket,
} from 'components/CallScribe/ticketCheck';
import { unsavedWork, type TUnsavedWork } from 'components/CallScribe/closeConfirm';
import { isContactScreen } from 'components/CallScribe/contactScreen';
import { notRecordedBody, offersRecheck } from 'components/CallScribe/notRecorded';
import axiosInstance from 'services/axiosInstance';
import { recordingStoppedLabel } from 'components/CallScribe/recordingStop';
import { customerNotesForServer, resolveSuggestedCustomerNotes } from 'components/CallScribe/reviewHelpers';
import { useTimeEntryOptions } from 'components/CallScribe/useTimeEntryOptions';

export interface IPostCallReviewProps {
  callId: number | undefined;
  crmSourceId?: number | null;
  documentationNotice?: string | undefined;
  onDismiss: () => void;
  onUnsavedChange?: (unsaved: TUnsavedWork | null) => void;
}

const POSTED_CLOSE_SECONDS = 3;

const STAGE_PROGRESS: Record<number, { percent: number; label: string }> = {
  [CallScribePipelineStage.Received]: { percent: 15, label: 'Preparing the recording' },
  [CallScribePipelineStage.Transcribing]: { percent: 50, label: 'Transcribing the call' },
  [CallScribePipelineStage.Summarizing]: { percent: 80, label: 'Writing the summary' },
};

type TSaveState = 'idle' | 'saving' | 'saved' | 'unsupported' | 'failed';

const AUTOSAVE_DEBOUNCE_MS = 700;

export const draftKey = (notes: string | undefined, minutes: number | undefined): string =>
  `${minutes ?? ''}\u0000${notes ?? '\u0001'}`;
const AUTOSAVE_FADE_MS = 2000;

export const reviewSubtitleParts = (call: ICallScribeCallListItem | undefined): string[] => {
  if (!call) return [];
  const who = call.contactName ?? formatPhoneE164ToDisplay(otherPartyPhone(call));
  const length = typeof call.durationSeconds === 'number' && call.durationSeconds > 0
    ? formatDurationLabel(call.durationSeconds)
    : '';
  const ticket = call.externalTicketId ? `Ticket #${call.externalTicketId}` : '';
  return [who, length, ticket].filter(Boolean);
};

export const reviewSubtitle = (call: ICallScribeCallListItem | undefined): string =>
  reviewSubtitleParts(call).join(' · ');

const PostCallReview: React.FC<IPostCallReviewProps> = ({
  callId,
  crmSourceId,
  documentationNotice,
  onDismiss,
  onUnsavedChange,
}) => {
  const classes = usePostCallReviewStyles();
  const paper = usePaperStyles();
  const config = useAppSelector(ConfigurationSelector);
  const { reviewCall, pipelineStage, pipelineError, pipelineStalled, loadFailed, refresh } = usePostCallIntelligence(
    callId,
    true,
  );

  const [duration, setDuration] = useState<number | undefined>(undefined);
  const [customerNotes, setCustomerNotes] = useState<string | undefined>(undefined);
  const [noteDestination, setNoteDestination] = useState<string | null>(null);
  const [skipCustomerNote, setSkipCustomerNote] = useState<boolean>(false);
  const [skipInternalNote, setSkipInternalNote] = useState<boolean>(false);
  const [internalDraft, setInternalDraft] = useState<string | null>(null);
  const [timeEntryText, setTimeEntryText] = useState<string | undefined>(undefined);
  const [skipTimeEntryText, setSkipTimeEntryText] = useState<boolean>(false);
  const [saveState, setSaveState] = useState<TSaveState>('idle');
  const [persistedKey, setPersistedKey] = useState<string | undefined>(undefined);
  const [saveMessage, setSaveMessage] = useState<string | undefined>(undefined);
  const [editingAttempt, setEditingAttempt] = useState(false);
  const [closeIn, setCloseIn] = useState<number | null>(null);
  const closeKept = useRef(false);
  const dismissRef = useRef(onDismiss);
  const saveSequence = useRef(0);
  const fadeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const fields = useTimeEntryOptions(crmSourceId, callId);

  const contactNote = readContactNote(reviewCall?.contactNote);
  const suggestedDuration = reviewCall?.suggestedDurationMinutes ?? null;
  const derivedDuration = fallbackDurationMinutes(
    reviewCall?.suggestedDurationMinutes,
    reviewCall?.durationSeconds,
  );
  const suggestedTimeEntryText = fields.options?.noteOverrides?.timeEntryTextPrefill ?? '';

  const {
    submit,
    discard,
    error: submitError,
    clearError,
    posting: isPosting,
    discarding,
  } = useTimeEntrySubmit(callId, refresh);
  const [ticketEntry, setTicketEntry] = useState('');
  const [ticketChecking, setTicketChecking] = useState(false);
  const [ticketError, setTicketError] = useState<string | undefined>(undefined);
  const [confirmedTicket, setConfirmedTicket] = useState<ICheckedTicket | undefined>(undefined);
  const namedTicket = confirmedTicket?.ticketId ?? (ticketError ? ticketEntry.trim() : '');

  const phaseInput = useMemo(() => ({
    hasIntelligence: reviewCall?.hasIntelligence,
    pipelineStage: pipelineStage ?? reviewCall?.pipelineStage,
    linkStatus: reviewCall?.linkStatus,
    consentResult: reviewCall?.consentResult,
    status: reviewCall?.status,
    answeredAtUtc: reviewCall?.answeredAtUtc,
    reconcileExhausted: pipelineStalled,
    willPostAutomatically: reviewCall?.willPostAutomatically,
    ticketless: !reviewCall?.externalTicketId && !namedTicket,
  }), [reviewCall, pipelineStage, pipelineStalled, namedTicket]);
  const ticketless = !reviewCall?.externalTicketId && !namedTicket;
  const phase = resolveReviewPhase(phaseInput);
  const suggestedCustomerNotes = phase === 'generating'
    ? ''
    : resolveSuggestedCustomerNotes(
      fields.options?.noteOverrides?.customerNotePrefill,
      reviewCall?.summaryCustomerFacing,
    );
  const offersManualSubmit = showsManualSubmit(phaseInput);
  const awaitingAutomaticPost = isAwaitingAutomaticPost(phaseInput);
  const declined = phase === 'declined';
  const failed = phase === 'failed';
  const ready = phase === 'ready';
  const posted = phase === 'posted';
  const loggedAttempt = isLoggedAttempt(phaseInput);

  // An error superseded by the success screen is still an error that rendered. Clear it the moment
  // the refreshed call says the entry is posted, rather than leaving it to be painted over.
  useEffect(() => {
    if (posted && submitError !== undefined) {
      clearError();
    }
  }, [posted, submitError, clearError]);

  const previousPhase = useRef(phase);
  const previousHasIntelligence = useRef(reviewCall?.hasIntelligence);
  useEffect(() => {
    const intelligenceArrived = previousHasIntelligence.current !== true &&
      reviewCall?.hasIntelligence === true;
    const leftGeneratingOrPending = (previousPhase.current === 'generating' ||
      previousPhase.current === 'pending') &&
      (phase === 'ready' || phase === 'posted');
    if (intelligenceArrived || leftGeneratingOrPending) {
      fields.reload();
    }
    previousPhase.current = phase;
    previousHasIntelligence.current = reviewCall?.hasIntelligence;
  }, [phase, reviewCall?.hasIntelligence, fields.reload]);
  const discarded = phase === 'discarded';
  const notConnected = phase === 'notConnected';
  const sipRejection = reviewCall?.sipFailureReason ?? null;
  const pending = phase === 'pending';
  const noSummary = phase === 'unavailable' ||
    phase === 'notConnected' ||
    (phase === 'generating' && pipelineStalled);
  const postFailed = reviewCall?.linkStatus === CallScribeTimeEntryStatus.Failed;
  const notRecordedReason = reviewCall?.notRecordedReason ?? null;
  const recheckable = offersRecheck(notRecordedReason, notConnected);

  // A call nobody answered has no duration to derive, and its entry was rendering a hard 0 with the
  // submit button disabled on `durationValue <= 0` — so the one case Masood explicitly asked to be
  // submittable was the one case that could not be submitted without the technician noticing the
  // zero and fixing it. One minute is the smallest truthful value for an attempt that happened, and
  // the field is editable as it always was.
  const seededDuration = derivedDuration > 0 ? derivedDuration : (noSummary ? 1 : 0);
  const durationValue = duration ?? seededDuration;
  const postedMinutes = postedDurationOf(reviewCall) ?? durationValue;
  const actionable = isReviewActionable(phase);

  const notDocumentable = documentationNotice ?? reviewCall?.documentationUnavailableReason ?? undefined;
  const notesValue = customerNotes ?? suggestedCustomerNotes;
  const overrides = fields.options?.noteOverrides;
  const notesState = customerNotesState(fields.options, fields.error, crmSourceId);
  const notesPosted = notesState === 'on';
  const mayChooseDestination = overrides?.allowDestinationOverride === true;
  const mayEditInternal = overrides?.allowInternalNoteEdit === true;
  const timeEntryTextEditable = overrides?.timeEntryTextEditable === true;
  const timeEntryTextValue = timeEntryText ?? suggestedTimeEntryText;
  const internalValue =
    mayEditInternal && internalDraft !== null
      ? internalDraft
      : reviewCall?.summaryInternal ?? null;
  const destinationValue =
    noteDestination ?? overrides?.defaultCustomerNoteDestination ?? '';
  const postedDestinationLine = destinationLine(
    reviewCall?.customerNoteOutcome,
    reviewCall?.customerNoteDestination,
    overrides?.customerNoteDestinations,
  );
  const postedFailureLine = failureLine(reviewCall?.customerNoteOutcome);

  // The note the trigger would post is the suggestion. Anything else is the technician's own text,
  // and the automatic path has no way to know about it — so an edit is precisely the thing that
  // turns "nothing is being asked of you" back into "there is something to submit".
  const customerNotesEdited =
    customerNotes !== undefined && customerNotes !== suggestedCustomerNotes;
  const technicianEditedTheEntry =
    customerNotesEdited ||
    (duration !== undefined && duration !== seededDuration);
  const offersSubmit = showsSubmitControl(phaseInput, technicianEditedTheEntry);

  const postedEntry = useRef({ posted: false, minutes: 0 });
  postedEntry.current = {
    posted: reviewCall?.linkStatus === CallScribeTimeEntryStatus.Posted,
    minutes: postedMinutes,
  };

  // What the server already holds from this review's own saves. The draft PATCH merges, so a field
  // the technician has not changed since it was saved is left out: re-sending a duration with every
  // note keystroke would overwrite a duration changed elsewhere (dev2 step 58: Add 5 minutes, then
  // typing a note, sent the 5 again with the note).
  const savedDraft = useRef<{ callId?: number; notes?: string; minutes?: number }>({});

  const runSave = useCallback(async (notes: string | undefined, minutes: number | undefined) => {
    if (typeof callId !== 'number') {
      return;
    }
    const saved = savedDraft.current.callId === callId ? savedDraft.current : {};
    const entry = postedEntry.current;
    const changedMinutes = minutes !== undefined && minutes > 0 && minutes !== saved.minutes
      ? minutes
      : undefined;
    // A posted entry is the exception, decided in 02565f4907: the ticket update re-sends the whole
    // entry, so every save after the post carries its minutes.
    const sentMinutes = entry.posted
      ? (minutes !== undefined && minutes > 0 ? minutes : entry.minutes > 0 ? entry.minutes : undefined)
      : changedMinutes;
    const sendNotes = notes !== undefined && notes !== saved.notes;
    const body = {
      ...(sendNotes ? { customerNotes: customerNotesForServer(notes) } : {}),
      ...(sentMinutes !== undefined ? { durationMinutes: sentMinutes } : {}),
    };
    if (Object.keys(body).length === 0) {
      // Back to exactly what this review last saved: nothing to send, and nothing unsaved either.
      if (draftKey(notes, minutes) === draftKey(saved.notes, saved.minutes)) {
        setPersistedKey(draftKey(notes, minutes));
      }
      return;
    }
    saveSequence.current += 1;
    const mine = saveSequence.current;
    setSaveState('saving');
    // Only the newest save moves the baseline. A stale one that lands late is forgotten, so the next
    // save sends its field again: a redundant field, never a lost one.
    const remember = (): void => {
      savedDraft.current = {
        callId,
        notes: sendNotes ? notes : saved.notes,
        minutes: sentMinutes ?? saved.minutes,
      };
    };
    try {
      const response = await patchCallScribeDraft(callId, body);
      if (mine !== saveSequence.current) {
        return;
      }
      const outcome = response?.data?.outcome;
      if (outcome === CallScribeDraftOutcome.SavedAndUpdatedTicket ||
        outcome === CallScribeDraftOutcome.SavedLocally ||
        outcome === CallScribeDraftOutcome.SavedButTicketUpdateUnsupported) {
        remember();
      }
      if (outcome === CallScribeDraftOutcome.SavedAndUpdatedTicket ||
        outcome === CallScribeDraftOutcome.SavedLocally) {
        setPersistedKey(draftKey(notes, minutes));
        setSaveMessage(undefined);
        setSaveState('saved');
      } else if (outcome === CallScribeDraftOutcome.SavedButTicketUpdateUnsupported) {
        setPersistedKey(draftKey(notes, minutes));
        setSaveMessage(response?.data?.message);
        setSaveState('unsupported');
      } else {
        setSaveMessage(response?.data?.message);
        setSaveState('failed');
      }
    } catch {
      if (mine === saveSequence.current) {
        setSaveMessage(undefined);
        setSaveState('failed');
      }
    }
  }, [callId]);

  const technicianTouchedTheEntry = customerNotes !== undefined || duration !== undefined;

  useEffect(() => {
    if (!technicianTouchedTheEntry || typeof callId !== 'number') {
      return undefined;
    }
    const timer = setTimeout(() => { void runSave(customerNotes, duration); }, AUTOSAVE_DEBOUNCE_MS);
    return () => { clearTimeout(timer); };
  }, [technicianTouchedTheEntry, customerNotes, duration, callId, runSave]);

  const unsavedEdit =
    technicianTouchedTheEntry && persistedKey !== draftKey(customerNotes, duration);

  const unsavedTicket =
    !reviewCall?.externalTicketId &&
    !posted &&
    (ticketEntry.trim().length > 0 || confirmedTicket !== undefined);

  const unsaved = unsavedWork(unsavedEdit, unsavedTicket);

  useEffect(() => {
    onUnsavedChange?.(unsaved);
  }, [unsaved, onUnsavedChange]);

  useEffect(() => {
    if (saveState !== 'saved') {
      return undefined;
    }
    fadeTimer.current = setTimeout(() => { setSaveState('idle'); }, AUTOSAVE_FADE_MS);
    return () => { if (fadeTimer.current) { clearTimeout(fadeTimer.current); } };
  }, [saveState]);

  const postedAutomatically =
    reviewCall?.willPostAutomatically === true &&
    reviewCall?.linkStatus === CallScribeTimeEntryStatus.Posted;

  const summaryDiffersFromNote =
    Boolean(reviewCall?.summaryCustomerFacing) &&
    notesValue.trim() !== (reviewCall?.summaryCustomerFacing ?? '').trim();

  const onCheckTicket = useCallback(async () => {
    const entered = ticketEntry.trim();
    if (!isUsableTicketNumber(entered)) {
      setConfirmedTicket(undefined);
      setTicketError(TICKET_NUMBER_REQUIRED);
      return;
    }
    setTicketChecking(true);
    setTicketError(undefined);
    const outcome = await checkTicket({
      psa: config?.psa,
      psaId: crmSourceId ?? config?.psaId ?? 0,
      ticketId: entered,
      fetchJson: async url => {
        const res = await axiosInstance.get<unknown>(url, { validateStatus: () => true });
        return { status: res.status, data: res.data };
      },
    });
    setTicketChecking(false);
    if (outcome.status === 'found') {
      setConfirmedTicket(outcome.ticket);
      setTicketError(undefined);
      return;
    }
    setConfirmedTicket(undefined);
    setTicketError(outcome.message);
  }, [ticketEntry, config?.psa, config?.psaId, crmSourceId]);

  const onSubmit = (): void => {
    submit({
      durationMinutes: durationValue,
      ...(namedTicket ? { externalTicketId: namedTicket } : {}),
      ...(customerNotesEdited ? { customerNotes: customerNotesForServer(notesValue) } : {}),
      internalNotes: internalValue,
      ...(noteDestination !== null
        ? { customerNoteDestination: noteDestination }
        : {}),
      ...(skipCustomerNote ? { skipCustomerNote: true } : {}),
      ...(skipInternalNote ? { skipInternalNote: true } : {}),
      ...(timeEntryText !== undefined && timeEntryText !== suggestedTimeEntryText
        ? { timeEntryText: timeEntryTextValue }
        : {}),
      ...(skipTimeEntryText ? { skipTimeEntryText: true } : {}),
      workTypeExternalId:
        fields.workType && fields.workType.length > 0 ? fields.workType : null,
      billingTypeExternalId:
        fields.billingType && fields.billingType.length > 0 ? fields.billingType : null,
    });
  };

  useEffect(() => {
    dismissRef.current = onDismiss;
  }, [onDismiss]);

  useEffect(() => {
    setCloseIn(phase === 'posted' && !closeKept.current ? POSTED_CLOSE_SECONDS : null);
  }, [phase]);

  useEffect(() => {
    if (closeIn === null) {
      return undefined;
    }
    if (closeIn <= 0) {
      dismissRef.current();
      return undefined;
    }
    const tick = window.setTimeout(() => {
      setCloseIn(left => (left === null ? null : left - 1));
    }, 1000);
    return () => { window.clearTimeout(tick); };
  }, [closeIn]);

  const keepOpen = useCallback((): void => {
    closeKept.current = true;
    setCloseIn(null);
  }, []);
  const keepOpenOnTouch = closeIn !== null ? keepOpen : undefined;

  const subtitleParts = reviewSubtitleParts(reviewCall);
  const judged = Boolean(reviewCall?.hasIntelligence);
  const sentimentChip = judged && reviewCall ? sentimentChipFor(reviewCall.sentiment) : null;

  const frame = (
    title: string,
    body: React.ReactNode,
    footer?: React.ReactNode,
    chip?: React.ReactNode,
  ): React.ReactElement => (
    <div
      className={classes.root}
      onPointerDownCapture={keepOpenOnTouch}
      onKeyDownCapture={keepOpenOnTouch}
      onFocusCapture={keepOpenOnTouch}
    >
      <div className={classes.head}>
        <div className={classes.titleBlock}>
          <h2 className={classes.title}>{title}</h2>
          {subtitleParts.length > 0 ? (
            <p className={classes.subtitle}>
              {subtitleParts.map((part, i) => (
                <React.Fragment key={part}>
                  {i > 0 ? ' · ' : null}
                  <span className={classes.subtitlePart}>{part}</span>
                </React.Fragment>
              ))}
            </p>
          ) : null}
        </div>
        {chip ? <span className={classes.headChip}>{chip}</span> : null}
        <button type="button" className={paper.ghostButton} onClick={onDismiss} disabled={isPosting}>
          <CloseRoundedIcon aria-hidden />
          Close
        </button>
      </div>
      <div className={classes.body}>{body}</div>
      {footer}
    </div>
  );

  const summarySection = (): React.ReactNode => (reviewCall && judged ? (
    <CallInsightsHeader call={reviewCall} variant="section" title="Summary">
      {reviewCall.summaryShort ? <p className={paper.body}>{reviewCall.summaryShort}</p> : null}
    </CallInsightsHeader>
  ) : null);

  if (!reviewCall) {
    if (pipelineStalled || loadFailed) {
      return frame('Review time entry', (
        <div className={`${paper.notice} ${paper.noticeBad}`} role="alert">
          <p className={paper.noticeTitle}>
            <ErrorOutlineRoundedIcon aria-hidden />
            This call could not be loaded
          </p>
          <p className={paper.noticeBody}>
            Nothing was posted. Try again, or find the call in the ticket&apos;s call log.
          </p>
          <button type="button" className={paper.textButton} onClick={() => { void refresh(); }}>
            <RefreshRoundedIcon aria-hidden />
            Try again
          </button>
        </div>
      ));
    }
    return frame('Review time entry', (
      <div className={classes.loading} role="status" aria-label="Loading the call" aria-busy="true">
        {[0, 1, 2].map(i => (
          <div key={i} className={`${paper.section} ${classes.loadingBlock}`} aria-hidden="true">
            <Skeleton variant="rounded" width="32%" height={12} animation="pulse" />
            <Skeleton variant="rounded" width="88%" height={16} animation="pulse" />
            <Skeleton variant="rounded" width="64%" height={16} animation="pulse" />
          </div>
        ))}
      </div>
    ));
  }

  if (sipRejection) {
    return frame("The call didn't go through", (
      <div className={`${paper.notice} ${paper.noticeBad}`} role="alert">
        <p className={paper.noticeTitle}>
          <ErrorOutlineRoundedIcon aria-hidden />
          {SIP_REJECTION_HEADLINE}
        </p>
        <p className={paper.noticeBody}>{sipRejectionDetail(sipRejection)}</p>
        <p className={paper.noticeBody}>{SIP_REJECTION_ACTION}</p>
      </div>
    ));
  }

  const workTypeLabel = fields.options?.workTypes?.find(o => o.value === fields.workType)?.label;

  const closingBar = closeIn !== null ? (
    <div className={classes.closingBar}>
      <span className={classes.closingText} aria-hidden>{`Closing in ${closeIn} s`}</span>
      <button type="button" className={paper.textButton} onClick={keepOpen}>Keep open</button>
      <span className={paper.srOnly} role="status">
        This window closes in a few seconds. Choose Keep open to stay.
      </span>
    </div>
  ) : undefined;

  if (posted && loggedAttempt && postedAutomatically) {
    return frame('Call attempt logged', (
      <>
        <div className={`${paper.notice} ${paper.noticeGood}`}>
          <p className={paper.noticeTitle}>
            <CheckCircleRoundedIcon aria-hidden />
            Nobody answered, so the ticket has a 0 min entry recording the attempt.
          </p>
          {workTypeLabel ? (
            <p className={paper.noticeBody}>Work type: {workTypeLabel}</p>
          ) : null}
        </div>
        {typeof callId === 'number' && editingAttempt ? (
          <section className={paper.section} aria-labelledby="cs-review-attempt-edit">
            <div className={paper.sectionHead}>
              <h3 id="cs-review-attempt-edit" className={paper.sectionLabel}>Edit this entry</h3>
            </div>
            <PostedTimeEntryEditor
              key={callId}
              callId={callId}
              initialMinutes={postedMinutes}
            />
          </section>
        ) : typeof callId === 'number' ? (
          <button type="button" className={paper.textButton} onClick={() => { setEditingAttempt(true); }}>
            Edit this entry
          </button>
        ) : null}
      </>
    ), closingBar);
  }

  if (posted) {
    return frame(postedAutomatically ? 'Time entry posted' : 'Time entry submitted', (
      <>
        <div className={`${paper.notice} ${paper.noticeGood}`}>
          <p className={paper.noticeTitle}>
            <CheckCircleRoundedIcon aria-hidden />
            {postedAutomatically
              ? 'This time entry was posted to the ticket automatically.'
              : successLine(
                  reviewCall?.customerNoteOutcome,
                  reviewCall?.internalNoteOutcome,
                )}
          </p>
          {postedDestinationLine ? (
            <p className={paper.noticeBody}>{postedDestinationLine}</p>
          ) : null}
          {postedFailureLine ? (
            <p className={paper.noticeBody} role="status">
              {postedFailureLine}
            </p>
          ) : null}
          {contactNote !== null ? (
            <p className={paper.noticeBody} role="status">
              Contact note: <strong>{contactNoteText(contactNote)}</strong>
            </p>
          ) : null}
        </div>
        {summarySection()}
        {typeof callId === 'number' ? (
          <section className={paper.section} aria-labelledby="cs-review-posted-edit">
            <div className={paper.sectionHead}>
              <h3 id="cs-review-posted-edit" className={paper.sectionLabel}>Edit the posted entry</h3>
            </div>
            <PostedTimeEntryEditor
              key={callId}
              callId={callId}
              initialMinutes={postedMinutes}
            />
          </section>
        ) : null}
      </>
    ), closingBar, sentimentChip);
  }

  if (discarded) {
    return frame('Suggestion discarded', (
      <>
        <div className={paper.notice}>
          <p className={paper.noticeBody}>
            No time entry was submitted. The call stays on the ticket&apos;s call log.
          </p>
        </div>
        {summarySection()}
      </>
    ), undefined, sentimentChip);
  }

  if (loggedAttempt && awaitingAutomaticPost) {
    return frame('Review time entry', (
      <div className={`${paper.notice} ${paper.noticeInfo}`} role="status">
        <p className={paper.noticeTitle}>
          <InfoOutlinedIcon aria-hidden />
          Logging this call attempt automatically
        </p>
        <p className={paper.noticeBody}>
          Nobody answered, so the ticket gets a 0 min entry recording the attempt. There is
          nothing for you to do.
        </p>
      </div>
    ));
  }

  const stageInfo = typeof pipelineStage === 'number' ? STAGE_PROGRESS[pipelineStage] : undefined;
  const generating = phase === 'generating' && !pipelineStalled;

  const blockedReason = !actionable
    ? 'Available once the summary is ready'
    : durationValue <= 0
      ? 'Enter a duration to submit'
      : undefined;

  const failures = (
    <>
      {submitError ? (
        <div className={`${paper.notice} ${paper.noticeBad}`} role="alert">
          <p className={paper.noticeTitle}>
            <ErrorOutlineRoundedIcon aria-hidden />
            That submission did not complete
          </p>
          <p className={paper.noticeBody}>{submitError}</p>
        </div>
      ) : null}
      {postFailed ? (
        <div className={`${paper.notice} ${paper.noticeBad}`} role="alert">
          <p className={paper.noticeTitle}>
            <ErrorOutlineRoundedIcon aria-hidden />
            The PSA rejected this time entry
          </p>
          <p className={paper.noticeBody}>
            {reviewCall?.linkErrorMessage ??
              'The PSA did not give a reason. Nothing was posted.'}
          </p>
          <p className={paper.noticeBody}>
            Nothing was posted. Adjust the entry and submit again.
          </p>
        </div>
      ) : null}
    </>
  );

  const footer = offersSubmit ? (
    <ActionBar
      label={offersManualSubmit ? 'Submit time entry' : 'Post my version now'}
      onAction={onSubmit}
      busy={isPosting || discarding}
      busyLabel={discarding ? 'Discarding' : 'Submitting'}
      disabledReason={isPosting || discarding ? undefined : blockedReason}
      secondary={offersManualSubmit
        ? { label: 'Discard', onAction: discard, disabled: isPosting || discarding || !actionable }
        : undefined}
    >
      {failures}
    </ActionBar>
  ) : awaitingAutomaticPost && !pending ? (
    <div className={classes.autoBar} role="status">
      <InfoOutlinedIcon aria-hidden />
      <p className={classes.autoText}>
        <strong>{AUTO_POST_TITLE}.</strong>
        {' '}{autoPostLine(notesPosted)}
      </p>
    </div>
  ) : undefined;

  return frame('Review time entry', (
    <>
      {notDocumentable ? (
        <div className={`${paper.notice} ${paper.noticeInfo}`} role="status">
          <p className={paper.noticeTitle}>
            <InfoOutlinedIcon aria-hidden />
            Not documented on the ticket
          </p>
          <p className={paper.noticeBody}>{notDocumentable}</p>
        </div>
      ) : null}

      {recordingStoppedLabel(reviewCall ?? {}) ? (
        <div className={paper.notice} role="status">
          <p className={paper.noticeBody}>{recordingStoppedLabel(reviewCall ?? {})}</p>
        </div>
      ) : null}

      {noSummary ? (
        <div className={`${paper.notice} ${paper.noticeWarn}`} role="status">
          <p className={paper.noticeTitle}>
            <InfoOutlinedIcon aria-hidden />
            {notConnected ? 'Nobody answered this call' : 'No summary for this call'}
          </p>
          <p className={paper.noticeBody}>
            {notRecordedBody(reviewCall?.notRecordedReasonText, notConnected)}
          </p>
          <p className={paper.noticeBody}>
            You can still submit a time entry below.
          </p>
          {recheckable ? (
            <button type="button" className={paper.textButton} onClick={() => { void refresh(); }}>
              <RefreshRoundedIcon aria-hidden />
              Check again
            </button>
          ) : null}
        </div>
      ) : null}

      {pending ? (
        <div className={`${paper.notice} ${paper.noticeInfo}`} role="status">
          <p className={paper.noticeTitle}>
            <InfoOutlinedIcon aria-hidden />
            {awaitingAutomaticPost ? 'Time entry' : 'Checking for a summary'}
          </p>
          <p className={paper.noticeBody}>
            {awaitingAutomaticPost
              ? notesPosted
                ? 'This time entry will be posted to the ticket automatically. You can still change ' +
                  'the customer-facing notes below, and we will post your version instead.'
                : 'This time entry will be posted to the ticket automatically.'
              : 'You can submit a time entry now, or wait for the summary.'}
          </p>
        </div>
      ) : null}

      {generating ? (
        <div className={`${paper.notice} ${paper.noticeInfo}`} role="status" aria-live="polite">
          <p className={paper.noticeTitle}>
            {stageInfo?.label ?? 'Preparing the summary'}
          </p>
          <div className={classes.progressTrack}>
            <div
              className={classes.progressFill}
              style={{ transform: `scaleX(${(stageInfo?.percent ?? 10) / 100})` }}
            />
          </div>
          <p className={paper.noticeBody}>
            You can close this window — the suggestion stays on the call in the ticket&apos;s call
            log, and nothing is lost.
          </p>
        </div>
      ) : null}

      {failed ? (
        <div className={`${paper.notice} ${paper.noticeBad}`} role="alert">
          <p className={paper.noticeTitle}>
            <ErrorOutlineRoundedIcon aria-hidden />
            The summary could not be produced
          </p>
          <p className={paper.noticeBody}>
            {pipelineError ??
              'The recording was captured but the summary failed. You can still submit a time entry with the duration and your own notes.'}
          </p>
          <button type="button" className={paper.textButton} onClick={() => { void refresh(); }}>
            <RefreshRoundedIcon aria-hidden />
            Try again
          </button>
        </div>
      ) : null}

      {declined ? (
        <div className={paper.notice} role="status">
          <p className={paper.noticeTitle}>Not recorded</p>
          <p className={paper.noticeBody}>
            The customer declined recording, so there is no transcript or summary for this call. You
            can still submit a time entry with the duration and your own notes.
          </p>
        </div>
      ) : null}

      {!reviewCall?.externalTicketId ? (
        <section className={paper.section} role="status" aria-labelledby="cs-review-ticket">
          <div className={paper.sectionHead}>
            <h3 id="cs-review-ticket" className={paper.sectionLabel}>Ticket</h3>
          </div>
          <p className={paper.small}>{ticketPromptFor(isContactScreen(config))}</p>
          <div className={classes.ticketRow}>
            <TextField
              className={`${paper.field} ${classes.ticketField}`}
              label="Ticket number"
              size="small"
              variant="outlined"
              InputLabelProps={{ shrink: true }}
              value={ticketEntry}
              onChange={e => {
                setTicketEntry(e.target.value.replace(/[^\d]/g, ''));
                setConfirmedTicket(undefined);
                setTicketError(undefined);
              }}
              onBlur={() => { void onCheckTicket(); }}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void onCheckTicket();
                }
              }}
              inputProps={{ 'aria-label': 'Ticket number', inputMode: 'numeric' }}
              disabled={ticketChecking}
            />
            <button
              type="button"
              className={`${paper.textButton} ${classes.ticketCheck}`}
              onClick={() => { void onCheckTicket(); }}
              disabled={ticketChecking}
            >
              {ticketChecking ? 'Checking' : 'Check'}
            </button>
          </div>
          {confirmedTicket ? (
            <p className={classes.confirmedTicket}>
              <CheckCircleRoundedIcon aria-hidden />
              {checkedTicketLabel(confirmedTicket)}
            </p>
          ) : null}
          {ticketError ? (
            <p className={classes.ticketError} role="alert">
              <WarningAmberRoundedIcon aria-hidden />
              {ticketError}
            </p>
          ) : null}
        </section>
      ) : null}

      {ready ? summarySection() : null}

      {ticketless ? null : (
        <TimeEntrySection
          idBase="cs-review"
          duration={durationValue}
          onDurationChange={setDuration}
          fields={fields}
          endedAtUtc={reviewCall?.endedAtUtc}
          suggestedMinutes={suggestedDuration}
        >
          {offersSubmit && timeEntryTextEditable ? (
            <div className={classes.subGroup} data-testid="cs-time-entry-text-override">
              <TextField
                className={paper.field}
                label="Time entry text"
                size="small"
                fullWidth
                multiline
                minRows={2}
                InputLabelProps={{ shrink: true }}
                disabled={skipTimeEntryText}
                value={timeEntryTextValue}
                onChange={e => {
                  setTimeEntryText(e.target.value);
                }}
                inputProps={{ 'aria-label': 'Time entry text' }}
              />
              <FormControlLabel
                className={paper.checkRow}
                control={
                  <Checkbox
                    size="small"
                    checked={!skipTimeEntryText}
                    onChange={e => {
                      setSkipTimeEntryText(!e.target.checked);
                    }}
                    inputProps={{ 'aria-label': 'Add text to the time entry' }}
                  />
                }
                label="Add text to the time entry"
              />
            </div>
          ) : null}
        </TimeEntrySection>
      )}

      <section className={paper.section} aria-labelledby="cs-review-customer-note">
        <div className={paper.sectionHead}>
          <h3 id="cs-review-customer-note" className={paper.sectionLabel}>Customer-facing notes</h3>
          {saveState === 'saved' ? (
            <span className={classes.autosaved} role="status">
              <CheckCircleRoundedIcon aria-hidden />
              Autosaved
            </span>
          ) : null}
        </div>
        {notesPosted ? (
          <>
            <p className={paper.caption}>
              This is what the customer sees. It starts from the call summary and you can change it.
            </p>
            <TextField
              className={paper.field}
              size="small"
              variant="outlined"
              fullWidth
              multiline
              minRows={3}
              value={notesValue}
              onChange={e => { setCustomerNotes(e.target.value); }}
              inputProps={{ 'aria-label': 'Customer-facing notes, editable' }}
              placeholder={
                generating ? 'Available once the summary is ready' : 'Add the note the customer will see'
              }
            />
          </>
        ) : notesState === 'loading' ? (
          <div className={paper.field} role="status" aria-label={NOTES_LOADING_LABEL}>
            <Skeleton variant="rounded" height={76} animation="pulse" />
          </div>
        ) : (
          <p className={paper.small}>{CUSTOMER_NOTES_OFF}</p>
        )}
        {notesPosted && ready && summaryDiffersFromNote ? (
          <div className={classes.original}>
            <p className={paper.caption}>Summary from the call</p>
            <p className={paper.small}>{reviewCall?.summaryCustomerFacing}</p>
          </div>
        ) : null}
        {saveState === 'unsupported' ? (
          <div className={`${paper.notice} ${paper.noticeWarn}`} role="status">
            <p className={paper.noticeTitle}>
              <InfoOutlinedIcon aria-hidden />
              Saved here, but the ticket still shows the original
            </p>
            <p className={paper.noticeBody}>
              {saveMessage ?? 'This PSA cannot update a posted time entry.'}
            </p>
          </div>
        ) : null}
        {saveState === 'failed' ? (
          <div className={`${paper.notice} ${paper.noticeWarn}`} role="status">
            <p className={paper.noticeTitle}>
              <WarningAmberRoundedIcon aria-hidden />
              Your last change was not saved
            </p>
            <p className={paper.noticeBody}>
              {saveMessage ?? 'Try again, or use the button below to put your version on the ticket.'}
            </p>
          </div>
        ) : null}
      </section>

      {mayEditInternal ? (
        <section className={paper.section} aria-labelledby="cs-review-internal-note">
          <div className={paper.sectionHead}>
            <h3 id="cs-review-internal-note" className={paper.sectionLabel}>Internal notes</h3>
          </div>
          <TextField
            className={paper.field}
            multiline
            size="small"
            fullWidth
            minRows={2}
            value={internalValue ?? ''}
            onChange={e => {
              setInternalDraft(e.target.value);
            }}
            inputProps={{ 'aria-label': 'Internal notes, editable' }}
          />
        </section>
      ) : (
        <section className={paper.section} aria-labelledby="cs-review-internal-note">
          <div className={paper.sectionHead}>
            <h3 id="cs-review-internal-note" className={paper.sectionLabel}>
              Internal notes
              <span className={paper.readOnlyChip}>Read-only</span>
            </h3>
          </div>
          <div
            className={paper.readOnlyBlock}
            role="region"
            aria-label="Internal notes, read-only"
            aria-readonly="true"
            tabIndex={0}
          >
            {reviewCall?.summaryInternal ??
              (generating || pending
                ? 'The internal note appears here once the summary is ready.'
                : 'No internal note was produced for this call.')}
          </div>
        </section>
      )}

      {offersSubmit && mayChooseDestination ? (
        <section className={paper.section} data-testid="cs-note-overrides" aria-labelledby="cs-review-destination">
          <div className={paper.sectionHead}>
            <h3 id="cs-review-destination" className={paper.sectionLabel}>Where the notes go</h3>
          </div>
          {notesPosted ? (
            <TextField
              select
              size="small"
              fullWidth
              className={paper.field}
              label="Where the customer note goes"
              InputLabelProps={{ shrink: true }}
              value={destinationValue}
              onChange={e => {
                setNoteDestination(e.target.value);
              }}
              inputProps={{ 'aria-label': 'Where the customer note goes' }}
            >
              {(overrides?.customerNoteDestinations ?? []).map(d => (
                <MenuItem key={d.value} value={d.value}>
                  {d.label}
                </MenuItem>
              ))}
            </TextField>
          ) : null}
          <div className={classes.checks}>
            {notesPosted ? (
              <FormControlLabel
                className={paper.checkRow}
                control={
                  <Checkbox
                    size="small"
                    checked={skipCustomerNote}
                    onChange={e => {
                      setSkipCustomerNote(e.target.checked);
                    }}
                    inputProps={{ 'aria-label': "Don't post the customer note" }}
                  />
                }
                label="Don't post the customer note"
              />
            ) : null}
            <FormControlLabel
              className={paper.checkRow}
              control={
                <Checkbox
                  size="small"
                  checked={skipInternalNote}
                  onChange={e => {
                    setSkipInternalNote(e.target.checked);
                  }}
                  inputProps={{ 'aria-label': "Don't post the internal note" }}
                />
              }
              label="Don't post the internal note"
            />
          </div>
        </section>
      ) : null}

      {offersSubmit ? null : failures}
    </>
  ), footer, ready ? sentimentChip : null);
};

export default PostCallReview;
